import { useState, useMemo } from "react";

/**
 * iter 37 — Bulk-select scaffolding for list tables.
 *
 * Wires up a Set<id> of selected rows + helpers for header / row checkboxes.
 * Bulk actions themselves are not implemented yet — this hook provides the
 * groundwork so future "Bulk Edit" / "Bulk Delete" features can drop in.
 *
 * Usage:
 *   const sel = useBulkSelect(items);
 *   <th><input type="checkbox" {...sel.allProps()} /></th>
 *   ...
 *   <td><input type="checkbox" {...sel.rowProps(it.id)} /></td>
 *
 * sel.selectedIds → array of ids currently checked.
 * sel.clear()     → uncheck everything (call after a bulk action completes).
 */
export default function useBulkSelect(items, opts = {}) {
  const testid = opts.testid || "row";
  const [selected, setSelected] = useState(() => new Set());

  const ids = useMemo(() => (items || []).map((i) => i.id), [items]);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSelected = !allSelected && ids.some((id) => selected.has(id));

  const toggleOne = (id) =>
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(ids));

  const allProps = () => ({
    type: "checkbox",
    checked: allSelected,
    ref: (el) => { if (el) el.indeterminate = someSelected; },
    onChange: toggleAll,
    "data-testid": `${testid}-select-all`,
    "aria-label": "Select all",
  });
  const rowProps = (id) => ({
    type: "checkbox",
    checked: selected.has(id),
    onChange: () => toggleOne(id),
    "data-testid": `${testid}-select-${id}`,
    "aria-label": `Select ${id}`,
  });

  return {
    selectedIds: Array.from(selected),
    selected,
    setSelected,
    clear: () => setSelected(new Set()),
    allProps,
    rowProps,
    allSelected,
    someSelected,
  };
}
